import React, {
  useEffect, useState, useContext, useRef, useCallback,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { AuthContext } from '../../context/AuthContext';
import './TrackResourcePlayer.css';

/* ================================================================
   § Constants & Helpers
   ================================================================ */
export const RESOURCE_ICONS = {
  playlist: '≡', video: '▶', article: '§', docs: '⊞', pdf: '⊟',
};
const RESOURCE_TYPE_LABEL = {
  playlist: 'Playlist', video: 'Video', article: 'Article', docs: 'Docs', pdf: 'PDF',
};

const extractYouTubeIds = (url) => {
  if (!url) return { vid: null, list: null };
  const vid = url.match(/^.*(youtu\.be\/|v\/|embed\/|watch\?v=|&v=)([^#&?]*).*/)?.[2] || null;
  const list = url.match(/[&?]list=([^&]+)/)?.[1] || null;
  return { vid, list };
};

const isChannelOnlyUrl = (url) => {
  if (!url) return false;
  const { vid, list } = extractYouTubeIds(url);
  if (vid || list) return false;
  return /youtube\.com\/(channel\/|c\/|@|user\/)/.test(url) ||
    /youtube\.com\/@/.test(url) || /youtube\.com\/c\//.test(url);
};

const isYouTubeUrl = (url) =>
  !!(url && (url.includes('youtube.com') || url.includes('youtu.be')));

/* ── Global YouTube IFrame API loader (singleton) ─────────────── */
let ytApiLoading = false;
let ytApiReady = false;
const ytReadyCallbacks = [];

const loadYouTubeAPI = (onReady) => {
  if (ytApiReady && window.YT?.Player) { onReady(); return; }
  ytReadyCallbacks.push(onReady);
  if (ytApiLoading) return;
  ytApiLoading = true;
  window.onYouTubeIframeAPIReady = () => {
    ytApiReady = true;
    ytReadyCallbacks.forEach(cb => cb());
    ytReadyCallbacks.length = 0;
  };
  const s = document.createElement('script');
  s.id = 'yt-api';
  s.src = 'https://www.youtube.com/iframe_api';
  document.body.appendChild(s);
};

/* ================================================================
   § Tab helpers
   ================================================================ */
const buildTabs = (hasProject, hasAssessment, isPlaylist) => [
  ...(isPlaylist ? [{ id: 'playlist', icon: '≡', label: 'Playlist' }] : []),
  { id: 'resources', icon: '§', label: 'Resources' },
  { id: 'notes', icon: '✎', label: 'Notes' },
  { id: 'quiz', icon: '✦', label: 'Quiz' },
  ...(hasProject ? [{ id: 'project', icon: '◈', label: 'Project' }] : []),
  ...(hasAssessment ? [{ id: 'assessment', icon: '◉', label: 'Assess' }] : []),
];

/* ================================================================
   § SidebarTabs
   ================================================================ */
const SidebarTabs = ({ active, onChange, hasProject, hasAssessment, isPlaylist }) => (
  <div className="trp-sidebar-tabs">
    {buildTabs(hasProject, hasAssessment, isPlaylist).map(tab => (
      <button
        key={tab.id}
        className={`trp-stab ${active === tab.id ? 'trp-stab--active' : ''}`}
        onClick={() => onChange(tab.id)}
        title={tab.label}
      >
        <span className="trp-stab-icon">{tab.icon}</span>
        <span className="trp-stab-label">{tab.label}</span>
      </button>
    ))}
  </div>
);

/* ================================================================
   § BottomNav (mobile)
   ================================================================ */
const BottomNav = ({ active, onChange, hasProject, hasAssessment, sidebarOpen, onToggleSidebar, isPlaylist }) => (
  <div className="trp-bottom-nav">
    <div className="trp-bottom-nav-inner">
      {buildTabs(hasProject, hasAssessment, isPlaylist).map(tab => (
        <button
          key={tab.id}
          className={`trp-bnav-btn ${active === tab.id && sidebarOpen ? 'trp-bnav-btn--active' : ''}`}
          onClick={() => {
            if (active === tab.id && sidebarOpen) onToggleSidebar(false);
            else { onChange(tab.id); onToggleSidebar(true); }
          }}
        >
          <span className="trp-bnav-icon">{tab.icon}</span>
          <span className="trp-bnav-label">{tab.label}</span>
        </button>
      ))}
    </div>
  </div>
);

/* ================================================================
   § ResourceRail
   ================================================================ */
const ResourceRail = ({ resources, currentIndex, completedIndices, onNavigate }) => (
  <div className="trp-rail-list">
    {resources.map((r, i) => {
      const isActive = currentIndex === i;
      const isDone = completedIndices?.includes(i);
      return (
        <button
          key={i}
          className={`trp-rail-item ${isActive ? 'trp-rail-item--active' : ''} ${isDone ? 'trp-rail-item--done' : ''}`}
          onClick={() => onNavigate(i)}
        >
          <span className="trp-ri-status">
            {isDone ? '✓' : isActive ? '▶' : <span className="trp-ri-num">{i + 1}</span>}
          </span>
          <div className="trp-ri-info">
            <span className="trp-ri-label">{r.label}</span>
            <div className="trp-ri-meta">
              <span className="trp-ri-type">{RESOURCE_TYPE_LABEL[r.type] || r.type}</span>
              {r.estimatedMinutes > 0 && <span className="trp-ri-dur">{r.estimatedMinutes}m</span>}
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

/* ================================================================
   § NotesPanel
   ================================================================ */
const NotesPanel = ({ notes, onAdd, onUpdate, onDelete, onSeek, getCurrentTime }) => {
  const fmt = (sec) => {
    if (sec == null) return null;
    const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  return (
    <div className="trp-panel trp-notes">
      <div className="trp-panel-header">
        <h3 className="trp-panel-title">Study Notes</h3>
        <div className="trp-panel-actions">
          <button className="trp-pill-btn" onClick={() => onAdd(getCurrentTime?.())}>+ Timestamp</button>
          <button className="trp-pill-btn" onClick={() => onAdd(null)}>+ Entry</button>
        </div>
      </div>
      <div className="trp-notes-body">
        {notes.length === 0 ? (
          <div className="trp-empty-state">
            <span className="trp-empty-icon">✎</span>
            <p>No notes yet. Capture key moments as you watch.</p>
          </div>
        ) : (
          notes.map((n, i) => (
            <div key={i} className="trp-note-card">
              {n.timeSec != null && (
                <button className="trp-note-time" onClick={() => onSeek?.(n.timeSec)}>
                  {fmt(n.timeSec)}
                </button>
              )}
              <textarea
                className="trp-note-text" value={n.content}
                placeholder="Write your note…"
                onChange={(e) => onUpdate(i, e.target.value)} rows={2}
              />
              <button className="trp-note-del" onClick={() => onDelete(i)}>✕</button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/* ================================================================
   § QuizPanel
   ================================================================ */
const QuizPanel = ({ isPro, resource, activePlaylistVideoId, onMarkComplete }) => {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resolveVideoId = () => {
    if (resource?.type === 'playlist')
      return resource?.playlistVideoId || activePlaylistVideoId || null;
    return resource?.url?.match(/^.*(youtu\.be\/|v\/|embed\/|watch\?v=|&v=)([^#&?]*).*/)?.[2] || null;
  };
  const videoId = resolveVideoId();

  const handleStart = async () => {
    if (!isPro || !videoId) return;
    try {
      setLoading(true);
      const res = await api.get(`/quiz/${videoId}?title=${encodeURIComponent(resource?.label || '')}`);
      setQuiz(res.data); setStarted(true);
    } catch { } finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const answersArr = Object.entries(answers).map(([qi, oi]) => ({
        questionIndex: parseInt(qi), selectedOption: oi,
      }));
      const res = await api.post('/quiz/submit', { quizId: quiz.quizId, videoId, answers: answersArr });
      setResults(res.data);
      if (res.data.passed) onMarkComplete?.();
    } catch { } finally { setSubmitting(false); }
  };

  const reset = () => { setResults(null); setStarted(false); setCurrentQ(0); setAnswers({}); };

  if (!isPro) return (
    <div className="trp-panel">
      <div className="trp-panel-header"><h3 className="trp-panel-title">Lesson Quiz</h3></div>
      <div className="trp-pro-gate">
        <span className="trp-pro-gate-icon">✦</span>
        <p>AI-generated quizzes are available on Pro.</p>
        <button className="trp-cta-btn" onClick={() => window.location.href = '/upgrade'}>Upgrade to Pro</button>
      </div>
    </div>
  );

  if (resource?.type === 'playlist' && !videoId) return (
    <div className="trp-panel">
      <div className="trp-panel-header"><h3 className="trp-panel-title">Lesson Quiz</h3></div>
      <div className="trp-empty-state" style={{ padding: '24px 16px' }}>
        <span className="trp-empty-icon">≡</span>
        <p>Play a video from this playlist to unlock a quiz for it.</p>
      </div>
    </div>
  );

  return (
    <div className="trp-panel">
      <div className="trp-panel-header">
        <h3 className="trp-panel-title">Lesson Quiz</h3>
        {quiz && <span className="trp-panel-badge">{quiz.questions.length}Q</span>}
      </div>
      <div className="trp-quiz-body">
        {loading ? (
          <div className="trp-loading-state"><div className="trp-mini-spin" /><p>Generating…</p></div>
        ) : results ? (
          <div className="trp-quiz-result">
            <div className={`trp-result-score ${results.passed ? 'pass' : 'fail'}`}>{results.score}%</div>
            <p className="trp-result-msg">{results.passed ? 'Great work!' : 'Keep studying!'}</p>
            <button className="trp-pill-btn" onClick={reset}>Try Again</button>
          </div>
        ) : !started ? (
          <div className="trp-quiz-kickoff">
            <span className="trp-kickoff-icon">✦</span>
            <h4>Knowledge Check</h4>
            <p>Test your understanding with AI-generated questions.</p>
            <button className="trp-cta-btn" onClick={handleStart}>Start Quiz</button>
          </div>
        ) : quiz ? (
          <div className="trp-quiz-active">
            <div className="trp-q-progress">
              {quiz.questions.map((_, i) => (
                <button key={i}
                  className={`trp-q-dot ${i === currentQ ? 'active' : ''} ${answers[i] !== undefined ? 'answered' : ''}`}
                  onClick={() => setCurrentQ(i)} />
              ))}
            </div>
            <p className="trp-q-label">Question {currentQ + 1} of {quiz.questions.length}</p>
            <h4 className="trp-q-text">{quiz.questions[currentQ].q}</h4>
            <div className="trp-q-opts">
              {quiz.questions[currentQ].options.map((opt, i) => (
                <button key={i}
                  className={`trp-q-opt ${answers[currentQ] === i ? 'selected' : ''}`}
                  onClick={() => setAnswers(prev => ({ ...prev, [currentQ]: i }))}>
                  <span className="trp-opt-letter">{String.fromCharCode(65 + i)}</span>
                  {opt}
                </button>
              ))}
            </div>
            <div className="trp-q-nav">
              {currentQ < quiz.questions.length - 1 ? (
                <button className="trp-cta-btn" onClick={() => setCurrentQ(p => p + 1)}
                  disabled={answers[currentQ] === undefined}>Next →</button>
              ) : (
                <button className="trp-cta-btn" onClick={handleSubmit}
                  disabled={submitting || answers[currentQ] === undefined}>
                  {submitting ? 'Submitting…' : 'Submit Quiz'}
                </button>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* ================================================================
   § ExternalResourceCard
   ================================================================ */
const ExternalResourceCard = ({ resource, showPlaylistNote }) => (
  <div className="trp-external-resource">
    <span className="trp-ext-icon">{RESOURCE_ICONS[resource?.type] || '↗'}</span>
    <h2 className="trp-ext-title">{resource?.label}</h2>
    {showPlaylistNote ? (
      <p className="trp-ext-desc">
        This playlist links to a YouTube channel. Open it to browse all videos —
        watch at your own pace, then mark complete.
      </p>
    ) : (
      <p className="trp-ext-desc">External resource. Click to open in a new tab.</p>
    )}
    <a href={resource?.url} target="_blank" rel="noreferrer" className="trp-cta-btn">
      Open {resource?.type === 'playlist' ? 'Playlist' : 'Resource'} ↗
    </a>
  </div>
);

/* ================================================================
   § PlaylistVideoRail — video list sidebar inside the player area
   ================================================================ */
const PlaylistVideoRail = ({ videos, activeVideoId, onSelect, completedVideoIds = [] }) => {
  const fmtDur = (sec) => {
    if (!sec) return '';
    const m = Math.floor(sec / 60), s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const activeRef = useRef(null);

  // Scroll active item into view
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeVideoId]);

  return (
    <div className="trp-panel">
      <div className="trp-panel-header">
        <h3 className="trp-panel-title">Playlist Progress</h3>
        <span className="trp-panel-badge ok">{completedVideoIds.length}/{videos.length}</span>
      </div>
      <div className="trp-sidebar-body" style={{ padding: '4px 0' }}>
        <div className="trp-rail-list">
          {videos.map((v, i) => {
            const isActive = activeVideoId === v.videoId;
            const isCompleted = completedVideoIds.includes(v.videoId);
            return (
              <button
                key={v.videoId || i}
                ref={isActive ? activeRef : null}
                className={`trp-rail-item ${isActive ? 'trp-rail-item--active' : ''} ${isCompleted ? 'trp-rail-item--done' : ''}`}
                onClick={() => onSelect(v.videoId)}
              >
                <span className="trp-ri-status">
                  {isActive ? '▶' : isCompleted ? '✓' : i + 1}
                </span>
                <div className="trp-ri-info">
                  <span className="trp-ri-label">{v.title}</span>
                  {v.durationSec > 0 && <span className="trp-ri-dur">{fmtDur(v.durationSec)}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   § useYouTubePlayer — custom hook that handles the YT lifecycle
   
   FIX: Uses a callback ref (setPlayerDiv) so the hook knows the exact
   moment the DOM node is available, eliminating all race conditions.
   ================================================================ */
function useYouTubePlayer({ videoId, onReady, onStateChange, onEnd }) {
  const playerRef = useRef(null);
  const [playerDiv, setPlayerDiv] = useState(null); // callback ref target

  useEffect(() => {
    if (!videoId || !playerDiv) return; // wait for BOTH videoId AND the DOM node

    let destroyed = false;

    const init = () => {
      if (destroyed) return;
      // Destroy any previous instance
      try { playerRef.current?.destroy(); } catch { }
      playerRef.current = new window.YT.Player(playerDiv, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1, controls: 1, fs: 1, playsinline: 1 },
        events: {
          onReady: (e) => { if (!destroyed) onReady?.(e); },
          onStateChange: (e) => {
            if (destroyed) return;
            onStateChange?.(e);
            if (e.data === window.YT.PlayerState.ENDED) onEnd?.();
          },
        },
      });
    };

    loadYouTubeAPI(init);

    return () => {
      destroyed = true;
      try { playerRef.current?.destroy(); } catch { }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, playerDiv]);

  const seekTo = useCallback((sec) => {
    playerRef.current?.seekTo(sec, true);
  }, []);

  const getCurrentTime = useCallback(() => {
    return playerRef.current?.getCurrentTime?.() ?? null;
  }, []);

  const getDuration = useCallback(() => {
    return playerRef.current?.getDuration?.() ?? null;
  }, []);

  // setPlayerDiv is used as a callback ref on the div: ref={setPlayerDiv}
  return { setPlayerDiv, seekTo, getCurrentTime, getDuration };
}

/* ================================================================
   § PlaylistPlayer
   ================================================================ */
function PlaylistPlayer({ resource, slug, phaseId, resIndex, activeVideoId, onVideoChange, onVideosLoaded, completedPlaylistVideos, onVideoCompleted, onComplete }) {
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [playlistError, setPlaylistError] = useState(null);
  const intervalRef = useRef(null);

  const { vid: directVid, list: playlistId } = extractYouTubeIds(resource?.url);
  const channelOnly = isChannelOnlyUrl(resource?.url);

  // ── Progress save ─────────────────────────────────────────────
  const saveProgress = useCallback(async (currentTime, duration) => {
    if (!currentTime) return;
    try {
      await api.post(`/tracks/${slug}/resource-progress`, {
        phaseId, resourceIndex: resIndex,
        watchedSec: currentTime,
        durationSec: duration,
        lastPlaylistVideoId: activeVideoId,
      });
    } catch { }
  }, [slug, phaseId, resIndex, activeVideoId]);

  // ── YouTube player via hook ───────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const { setPlayerDiv, seekTo: _seekTo, getCurrentTime, getDuration } = useYouTubePlayer({
    videoId: activeVideoId || directVid || null,
    onReady: () => { },
    onStateChange: (e) => {
      if (e.data === window.YT.PlayerState.PLAYING) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            saveProgress(getCurrentTime(), getDuration());
          }, 8000);
        }
      } else {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
    onEnd: () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;

      const currentVideoId = activeVideoId;
      onVideoCompleted?.(currentVideoId);

      // Save completed to DB
      api.post(`/tracks/${slug}/resource-progress`, {
        phaseId, resourceIndex: resIndex,
        completedPlaylistVideoId: currentVideoId
      }).catch(() => { });

      // Auto-advance
      setPlaylistVideos(prev => {
        const idx = prev.findIndex(v => v.videoId === activeVideoId);
        if (idx >= 0 && idx < prev.length - 1) {
          onVideoChange?.(prev[idx + 1].videoId);
        } else {
          onComplete?.();
        }
        return prev;
      });
    },
  });

  useEffect(() => () => { clearInterval(intervalRef.current); }, []);



  // ── Load playlist videos from backend ────────────────────────
  useEffect(() => {
    if (!playlistId) return;
    setLoadingVideos(true);
    setPlaylistError(null);

    api.get(`/tracks/playlist-videos?listId=${playlistId}`)
      .then(({ data }) => {
        const videos = data.videos || [];
        if (videos.length === 0) {
          throw new Error("No videos found");
        }
        setPlaylistVideos(videos);
        onVideosLoaded?.(videos);
        // Set first video if no resume point
        if (!activeVideoId && videos.length > 0) {
          onVideoChange?.(videos[0].videoId);
        }
      })
      .catch(() => {
        setPlaylistError('Could not load playlist videos.');
        onVideosLoaded?.([]);
        // Still allow opening on YouTube
      })
      .finally(() => setLoadingVideos(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);



  // ── Channel-only URL — can't embed ───────────────────────────
  if (channelOnly) return <ExternalResourceCard resource={resource} showPlaylistNote />;

  if (directVid && !playlistId) {
    return (
      <div className="trp-playlist-layout trp-playlist-layout--single">
        <div className="trp-playlist-player-wrap">
          <div style={{ width: '100%', height: '100%' }}>
            <div ref={setPlayerDiv} className="trp-yt-frame" />
          </div>
        </div>
      </div>
    );
  }

  // ── Full playlist — needs listId ─────────────────────────────
  if (playlistError && playlistVideos.length === 0) {
    return (
      <div className="trp-playlist-layout">
        <div className="trp-playlist-player-wrap">
          <ExternalResourceCard resource={resource} showPlaylistNote />
        </div>
      </div>
    );
  }

  const activeVideoObj = playlistVideos.find(v => v.videoId === activeVideoId);

  return (
    <div className="trp-playlist-layout" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Video Title Bar */}
      {activeVideoObj && (
        <div style={{ padding: '14px 20px', background: 'var(--trp-bg)', borderBottom: '1px solid var(--trp-border-soft)', flexShrink: 0 }}>
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--trp-text)', margin: 0, lineHeight: 1.4 }}>
            {activeVideoObj.title}
          </h2>
        </div>
      )}

      {/* Player */}
      <div className="trp-playlist-player-wrap" style={{ flex: 1 }}>
        {loadingVideos && !activeVideoId ? (
          <div className="trp-pl-loading">
            <div className="trp-mini-spin" />
            <p>Loading playlist…</p>
          </div>
        ) : activeVideoId ? (
          /* KEY on activeVideoId ensures ref callback fires when video changes */
          <div key={activeVideoId} style={{ width: '100%', height: '100%' }}>
            <div ref={setPlayerDiv} className="trp-yt-frame" />
          </div>
        ) : (
          <div className="trp-pl-empty">
            <span className="trp-empty-icon">▶</span>
            <p>Select a video below to start.</p>
          </div>
        )}
      </div>

      {/* Video list rail has been moved to sidebar tab */}

      {/* Loading skeleton */}
      {loadingVideos && playlistVideos.length === 0 && (
        <div className="trp-pl-loading">
          <div className="trp-mini-spin" />
        </div>
      )}

      {/* Error fallback (but videos already loaded from cache? show them anyway) */}
      {playlistError && playlistVideos.length > 0 && (
        <p className="trp-pl-error">⚠ Partial load — some videos may be missing.</p>
      )}
    </div>
  );
}

/* ================================================================
   § Main Component
   ================================================================ */
export default function TrackResourcePlayer() {
  const { slug, phaseId, resourceIndex } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const intervalRef = useRef(null);

  const [track, setTrack] = useState(null);
  const [phase, setPhase] = useState(null);
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [resumeTime, setResumeTime] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('resources');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [activePlaylistVideoId, setActivePlaylistVideoId] = useState(null);
  const [playlistVideos, setPlaylistVideos] = useState([]);
  const [completedPlaylistVideos, setCompletedPlaylistVideos] = useState([]);

  const isPro =
    user?.subscription?.plan === 'PRO' &&
    (user?.subscription?.status === 'ACTIVE' ||
      (user?.subscription?.currentPeriodEnd &&
        new Date(user.subscription.currentPeriodEnd) > new Date()));
  const resIndex = parseInt(resourceIndex);

  const isPlaylist = resource?.type === 'playlist';
  const isYoutube = isYouTubeUrl(resource?.url);
  const isEmbeddableYT = isYoutube && !isChannelOnlyUrl(resource?.url) && !isPlaylist;

  /* ── Fetch ──────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/tracks/${slug}`);
        setTrack(data.track);

        const currentPhase = data.track.phases.find(p => p._id === phaseId);
        if (!currentPhase) { setError('Phase not found'); setLoading(false); return; }
        setPhase(currentPhase);

        const currentResource = currentPhase.resources?.[resIndex];
        if (!currentResource) { setError('Resource not found'); setLoading(false); return; }
        setResource(currentResource);

        const idxInTrack = data.track.phases.findIndex(p => p._id === phaseId);
        const isFree = idxInTrack < (data.track.freePhaseCount || 1);
        if (!isFree && !isPro) { setError('UPGRADE_REQUIRED'); setLoading(false); return; }
        if (!data.enrollment) { setError('NOT_ENROLLED'); setLoading(false); return; }
        if (currentPhase.locked) { setError(isPro ? 'SEQUENTIAL_LOCKED' : 'UPGRADE_REQUIRED'); setLoading(false); return; }

        try {
          const progRes = await api.get(`/tracks/${slug}/resource/${phaseId}/${resIndex}/progress`);
          if (progRes.data) {
            setResumeTime(progRes.data.watchedSec || 0);
            setIsCompleted(progRes.data.completed || false);
            setActivePlaylistVideoId(progRes.data.lastPlaylistVideoId || null);
            setCompletedPlaylistVideos(progRes.data.completedPlaylistVideos || []);
          }

          if (currentResource.type === 'playlist') {
            setSidebarTab('playlist');
          }
        } catch { }

        const storageKey = `note_${slug}_${phaseId}_${resIndex}`;
        const saved = localStorage.getItem(storageKey);
        setNotes(saved ? JSON.parse(saved) : []);
        setLoading(false);
      } catch {
        setError('Failed to load resource');
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, phaseId, resourceIndex, isPro]);

  /* ── Reset on resource change ───────────────────────────────── */
  useEffect(() => {
    setSidebarTab('resources');
    setSidebarOpen(false);
    setActivePlaylistVideoId(null);
  }, [resourceIndex]);

  /* ── Escape closes sidebar ─────────────────────────────────── */
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  /* ── YouTube player for single (non-playlist) videos ──────── */
  const { setPlayerDiv, seekTo, getCurrentTime, getDuration } = useYouTubePlayer({
    videoId: isEmbeddableYT ? (extractYouTubeIds(resource?.url).vid || null) : null,
    onReady: (e) => { if (resumeTime > 0) e.target.seekTo(resumeTime, true); },
    onStateChange: (e) => {
      if (e.data === window.YT.PlayerState.PLAYING) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(async () => {
            try {
              await api.post(`/tracks/${slug}/resource-progress`, {
                phaseId, resourceIndex: resIndex,
                watchedSec: getCurrentTime(),
                durationSec: getDuration(),
              });
            } catch { }
          }, 8000);
        }
      } else {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    },
    onEnd: () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      markCompleted();
    },
  });
  useEffect(() => () => { clearInterval(intervalRef.current); }, []);

  /* ── markCompleted ─────────────────────────────────────────── */
  const markCompleted = useCallback(async () => {
    setIsCompleted(true);
    try {
      await api.post(`/tracks/${slug}/resource-progress`, {
        phaseId, resourceIndex: resIndex,
        watchedSec: getCurrentTime() || 0,
        completed: true,
      });
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, phaseId, resIndex]);

  /* ── Notes ─────────────────────────────────────────────────── */
  const saveNotes = (updated) => {
    localStorage.setItem(`note_${slug}_${phaseId}_${resIndex}`, JSON.stringify(updated));
  };
  const addNote = (time) => { const u = [...notes, { timeSec: time ?? null, content: '', createdAt: new Date() }]; setNotes(u); saveNotes(u); };
  const updateNote = (i, val) => { const u = [...notes]; u[i].content = val; setNotes(u); saveNotes(u); };
  const deleteNote = (i) => { const u = notes.filter((_, idx) => idx !== i); setNotes(u); saveNotes(u); };

  /* ── Error gates ────────────────────────────────────────────── */
  if (loading) return (
    <div className="trp-page trp-page--loading">
      <div className="trp-spinner" /><p>Loading resource…</p>
    </div>
  );
  if (error === 'SEQUENTIAL_LOCKED') return (
    <div className="trp-page trp-page--gate">
      <div className="trp-gate-card">
        <span className="trp-gate-icon">⏳</span>
        <h2>Complete the Previous Phase</h2>
        <p>Work through the previous phase step by step — that's how the track is designed.</p>
        <button className="trp-cta-btn" onClick={() => navigate(`/tracks/${slug}`)}>Back to Track</button>
      </div>
    </div>
  );
  if (error === 'UPGRADE_REQUIRED') return (
    <div className="trp-page trp-page--gate">
      <div className="trp-gate-card">
        <span className="trp-gate-icon">◈</span>
        <h2>Pro Phase</h2>
        <p>Upgrade to Pro to access this phase, including all resources, projects, and AI assessments.</p>
        <button className="trp-cta-btn" onClick={() => navigate('/upgrade')}>Upgrade to Pro</button>
        <button className="trp-link-btn" onClick={() => navigate(`/tracks/${slug}`)}>Back to Track</button>
      </div>
    </div>
  );
  if (error === 'NOT_ENROLLED') return (
    <div className="trp-page trp-page--gate">
      <div className="trp-gate-card">
        <span className="trp-gate-icon">◉</span>
        <h2>Enroll First</h2>
        <p>You need to enroll in this track before you can start learning.</p>
        <button className="trp-cta-btn" onClick={() => navigate(`/tracks/${slug}`)}>Go to Track</button>
      </div>
    </div>
  );
  if (error) return (
    <div className="trp-page trp-page--gate">
      <div className="trp-gate-card">
        <h2>{error}</h2>
        <button className="trp-link-btn" onClick={() => navigate(`/tracks/${slug}`)}>Back</button>
      </div>
    </div>
  );

  /* ── Derived values ─────────────────────────────────────────── */
  const isLastResource = !phase.resources[resIndex + 1];
  const pp = phase.phaseProgress;
  const completedResources = pp?.completedResources || [];
  const totalRes = phase.resources?.length || 1;
  const phasePercent = Math.min(100, Math.round((completedResources.length / totalRes) * 100));
  const hasProject = !!phase.project;
  const hasAssessment = (phase.assessmentVideoIds?.length > 0) && !phase.locked;

  const handleNavigate = (idx) => {
    navigate(`/tracks/${slug}/resource/${phaseId}/${idx}`);
    setSidebarOpen(false);
  };

  /* ── Content area ───────────────────────────────────────────── */
  const renderContentArea = () => {
    if (isPlaylist) {
      return (
        <PlaylistPlayer
          resource={resource}
          slug={slug}
          phaseId={phaseId}
          resIndex={resIndex}
          activeVideoId={activePlaylistVideoId}
          onVideoChange={setActivePlaylistVideoId}
          onVideosLoaded={setPlaylistVideos}
          completedPlaylistVideos={completedPlaylistVideos}
          onVideoCompleted={(vid) => {
            if (!completedPlaylistVideos.includes(vid)) {
              setCompletedPlaylistVideos(prev => [...prev, vid]);
            }
          }}
          onComplete={markCompleted}
        />
      );
    }
    if (isEmbeddableYT) {
      return <div ref={setPlayerDiv} className="trp-yt-frame" />;
    }
    return <ExternalResourceCard resource={resource} />;
  };

  /* ── Sidebar content ────────────────────────────────────────── */
  const renderSidebarContent = () => {
    switch (sidebarTab) {
      case 'playlist':
        return (
          <PlaylistVideoRail
            videos={playlistVideos}
            activeVideoId={activePlaylistVideoId}
            completedVideoIds={completedPlaylistVideos}
            onSelect={(vid) => setActivePlaylistVideoId(vid)}
          />
        );
      case 'resources':
        return (
          <div className="trp-panel">
            <div className="trp-panel-header">
              <div>
                <h3 className="trp-panel-title">{phase.title}</h3>
                <p className="trp-panel-sub">{completedResources.length} of {totalRes} complete</p>
              </div>
              <span className="trp-panel-badge">{phasePercent}%</span>
            </div>
            <ResourceRail
              resources={phase.resources}
              currentIndex={resIndex}
              completedIndices={completedResources}
              onNavigate={handleNavigate}
            />
            {(hasProject || hasAssessment) && (
              <div className="trp-milestones">
                <p className="trp-milestones-label">Phase milestones</p>
                {hasAssessment && (
                  <button
                    className={`trp-milestone-item ${pp?.quizScore >= 70 ? 'done' : ''}`}
                    onClick={() => isPro
                      ? navigate(`/tracks/${slug}/assessment/${phaseId}`)
                      : navigate('/upgrade')}>
                    <span className="trp-ms-icon">◉</span>
                    <div>
                      <span className="trp-ms-name">AI Assessment</span>
                      {pp?.quizScore != null
                        ? <span className="trp-ms-status">{pp.quizScore}%</span>
                        : <span className="trp-ms-status pending">Required</span>}
                    </div>
                  </button>
                )}
                {hasProject && (
                  <button
                    className={`trp-milestone-item ${pp?.projectLogged ? 'done' : ''}`}
                    onClick={() => navigate(`/tracks/${slug}/project/${phaseId}`)}>
                    <span className="trp-ms-icon">◈</span>
                    <div>
                      <span className="trp-ms-name">{phase.project.title}</span>
                      <span className="trp-ms-status">{pp?.projectLogged ? 'Submitted' : 'Project'}</span>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        );
      case 'notes':
        return (
          <NotesPanel notes={notes} onAdd={addNote} onUpdate={updateNote}
            onDelete={deleteNote} onSeek={seekTo} getCurrentTime={getCurrentTime} />
        );
      case 'quiz':
        return (
          <QuizPanel isPro={isPro} resource={resource}
            activePlaylistVideoId={activePlaylistVideoId} onMarkComplete={markCompleted} />
        );
      case 'project':
        if (!hasProject) return null;
        return (
          <div className="trp-panel">
            <div className="trp-panel-header">
              <h3 className="trp-panel-title">Phase Project</h3>
              {pp?.projectLogged && <span className="trp-panel-badge ok">Submitted</span>}
            </div>
            <div className="trp-project-info">
              <h4 className="trp-project-title">{phase.project.title}</h4>
              <p className="trp-project-desc">{phase.project.description}</p>
              {phase.project.requiredSkills?.length > 0 && (
                <div className="trp-skill-chips">
                  {phase.project.requiredSkills.map(s => (
                    <span key={s} className="trp-skill-chip">{s}</span>
                  ))}
                </div>
              )}
              <button className="trp-cta-btn" style={{ marginTop: 12, width: '100%' }}
                onClick={() => navigate(`/tracks/${slug}/project/${phaseId}`)}>
                {pp?.projectLogged ? 'View Submission →' : 'Submit Project →'}
              </button>
            </div>
          </div>
        );
      case 'assessment':
        if (!hasAssessment) return null;
        return (
          <div className="trp-panel">
            <div className="trp-panel-header">
              <h3 className="trp-panel-title">Phase Assessment</h3>
              {pp?.quizScore >= 70 && <span className="trp-panel-badge ok">Passed</span>}
            </div>
            <div className="trp-assessment-info">
              <p className="trp-assessment-desc">
                Deep assessment for Phase {phase.order}. Need 70%+ to complete the phase.
              </p>
              {pp?.quizScore != null && (
                <div className="trp-score-display">
                  <span className="trp-score-val">{pp.quizScore}%</span>
                  <span className="trp-score-label">Last attempt</span>
                </div>
              )}
              <button className="trp-cta-btn" style={{ marginTop: 12, width: '100%' }}
                onClick={() => navigate(`/tracks/${slug}/assessment/${phaseId}`)}>
                {pp?.quizScore != null ? 'Retake Assessment →' : 'Start Assessment →'}
              </button>
            </div>
          </div>
        );
      default: return null;
    }
  };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className="trp-page">

      {/* Top bar */}
      <header className="trp-topbar">
        <button className="trp-back-btn" onClick={() => navigate(`/tracks/${slug}`)}>
          ← <span>{track?.title}</span>
        </button>
        <div className="trp-topbar-center">
          <span className="trp-phase-crumb">Phase {phase.order}: {phase.title}</span>
          <span className="trp-res-crumb">{resource?.label}</span>
        </div>
        <div className="trp-topbar-right">
          {isCompleted && <span className="trp-completed-chip">✓ Done</span>}
          <div className="trp-phase-prog" title={`${phasePercent}% complete`}>
            <div className="trp-phase-prog-fill" style={{ width: `${phasePercent}%` }} />
          </div>
          <span className="trp-prog-label">{completedResources.length}/{totalRes}</span>
          <button className="trp-sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} aria-label="Toggle sidebar">
            {sidebarOpen ? '✕' : '≡'}
          </button>
        </div>
      </header>

      {/* Main */}
      <div className="trp-main">

        {/* Video + infobar */}
        <div className="trp-video-area">
          <div className={`trp-player-wrap ${isPlaylist ? 'trp-player-wrap--playlist' : ''}`}>
            {renderContentArea()}
          </div>

          <div className="trp-infobar">
            <div className="trp-infobar-left">
              <h1 className="trp-res-title">{resource?.label}</h1>
              <div className="trp-res-meta">
                <span className="trp-res-type-badge">{RESOURCE_TYPE_LABEL[resource?.type] || resource?.type}</span>
                {resource?.estimatedMinutes > 0 && (
                  <span className="trp-res-duration">{resource.estimatedMinutes} min</span>
                )}
                {resumeTime > 0 && !isCompleted && !isPlaylist && (
                  <span className="trp-resume-hint">Resuming…</span>
                )}
                {isPlaylist && activePlaylistVideoId && (
                  <span className="trp-resume-hint">Now playing</span>
                )}
              </div>
            </div>
            <div className="trp-infobar-right">
              {!isCompleted && (
                <button className="trp-mark-btn" onClick={markCompleted}>✓ Mark Complete</button>
              )}
              <button className="trp-next-btn"
                onClick={() => isLastResource ? navigate(`/tracks/${slug}`) : handleNavigate(resIndex + 1)}>
                {isLastResource ? 'Finish →' : 'Next →'}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {sidebarOpen && <div className="trp-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        <aside className={`trp-sidebar ${sidebarOpen ? 'trp-sidebar--open' : ''}`}>
          <SidebarTabs
            active={sidebarTab}
            onChange={setSidebarTab}
            hasProject={hasProject}
            hasAssessment={hasAssessment && isPro}
            isPlaylist={isPlaylist}
          />
          <div className="trp-sidebar-body">
            {renderSidebarContent()}
          </div>
        </aside>
      </div>

      {/* Mobile bottom nav */}
      <BottomNav
        active={sidebarTab}
        onChange={setSidebarTab}
        hasProject={hasProject}
        hasAssessment={hasAssessment && isPro}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={setSidebarOpen}
        isPlaylist={isPlaylist}
      />
    </div>
  );
}